"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { formatRegionalTime } from "@/lib/i18n/config/languages";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/use-translation";
import {
  getRenderCounts,
  resetRenderCounts,
  subscribeRenderCounterStore,
  type RenderCountEntry,
} from "../stores/render-counter-store";

export function RenderCounterPanel() {
  const t = useTranslations("debug.renders");
  const { formatLocale, timeZone } = useTranslation("common");
  const [counts, setCounts] = useState<RenderCountEntry[]>(() =>
    getRenderCounts(),
  );
  const [expandedComponent, setExpandedComponent] = useState<string | null>(
    null,
  );
  const panelRenderCount = 1;

  useEffect(() => {
    return subscribeRenderCounterStore(() => {
      setCounts([...getRenderCounts()]);
    });
  }, []);

  const handleReset = useCallback(() => {
    resetRenderCounts();
    setExpandedComponent(null);
  }, []);

  const toggleExpand = (name: string) => {
    setExpandedComponent((current) => (current === name ? null : name));
  };

  const [copied, setCopied] = useState(false);

  const handleCopyJson = useCallback(() => {
    const payload = {
      panelRenders: panelRenderCount,
      trackedComponentsCount: counts.length,
      timestamp: new Date().toISOString(),
      components: counts.map((c) => ({
        name: c.name,
        count: c.count,
        status: c.status,
        firstRenderedAt: new Date(c.firstRenderedAt).toISOString(),
        lastRenderedAt: new Date(c.lastRenderedAt).toISOString(),
        avgDeltaMs: c.avgDeltaMs,
        history: c.history.map((h) => ({
          pass: `#${h.id}`,
          timestamp: new Date(h.timestamp).toISOString(),
          deltaMs: h.deltaMs,
          note: h.note || null,
        })),
      })),
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [counts]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-muted-foreground space-y-0.5">
          <div>
            {t.rich("panelRenderCount", {
              count: panelRenderCount,
              value: (chunks) => (
                <span className="font-mono font-semibold text-foreground">
                  {chunks}
                </span>
              ),
            })}
          </div>
          {counts.length > 0 && (
            <div>{t("trackedCount", { count: counts.length })}</div>
          )}
        </div>
        {counts.length > 0 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleCopyJson}
              className="cursor-pointer rounded border border-border bg-primary/10 px-2 py-1 font-mono text-sm text-primary transition-colors hover:bg-primary/20"
            >
              {copied ? t("copied") : t("copyJson")}
            </button>
            <button
              onClick={handleReset}
              className="cursor-pointer rounded border border-border bg-muted/50 px-2 py-1 font-mono text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("reset")}
            </button>
          </div>
        )}
      </div>

      {counts.length === 0 ? (
        <div className="text-sm text-muted-foreground bg-muted/20 rounded-lg p-3 space-y-1">
          <div className="font-medium">{t("trackingGuide")}</div>
          <pre className="text-sm font-mono mt-1 text-foreground/70">
            {t("usageExample")}
          </pre>
        </div>
      ) : (
        <div className="space-y-1">
          {counts.map((entry) => {
            const isExpanded = expandedComponent === entry.name;
            return (
              <div
                key={entry.name}
                className="rounded border border-border/40 bg-muted/10 overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(entry.name)}
                  className="w-full flex items-center justify-between gap-2 py-2 px-2.5 text-sm hover:bg-muted/30 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono font-medium text-foreground truncate">
                      {entry.name}
                    </span>
                    {entry.status === "rapid" ? (
                      <span className="rounded border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.2 font-mono text-sm text-amber-500">
                        {t("rapid")}
                      </span>
                    ) : (
                      <span className="rounded border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.2 font-mono text-sm text-emerald-500">
                        {t("stable")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0 font-mono text-sm">
                    <span className="font-bold text-foreground">
                      {entry.count}
                      {"×"}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatRegionalTime(
                        entry.lastRenderedAt,
                        formatLocale,
                        timeZone,
                      )}
                    </span>
                    <span className="text-sm text-muted-foreground/60">
                      {isExpanded ? "▲" : "▼"}
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="space-y-3 border-t border-border/30 bg-muted/30 p-3 font-mono text-sm">
                    {/* Stat Micro Cards */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded border border-border/40 bg-background/60 p-2 text-center">
                        <div className="text-sm uppercase tracking-wider text-muted-foreground">
                          {t("firstRender")}
                        </div>
                        <div className="mt-0.5 text-sm font-semibold text-foreground">
                          {formatRegionalTime(
                            entry.firstRenderedAt,
                            formatLocale,
                            timeZone,
                          )}
                        </div>
                      </div>
                      <div className="rounded border border-border/40 bg-background/60 p-2 text-center">
                        <div className="text-sm uppercase tracking-wider text-muted-foreground">
                          {t("averageInterval")}
                        </div>
                        <div className="mt-0.5 text-sm font-bold text-primary">
                          {t("milliseconds", { count: entry.avgDeltaMs })}
                        </div>
                      </div>
                      <div className="rounded border border-border/40 bg-background/60 p-2 text-center">
                        <div className="text-sm uppercase tracking-wider text-muted-foreground">
                          {t("totalRenders")}
                        </div>
                        <div className="mt-0.5 text-sm font-bold text-foreground">
                          {t("renderCount", { count: entry.count })}
                        </div>
                      </div>
                    </div>

                    {/* Styled Vertical Timeline */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-sm font-semibold text-muted-foreground">
                        <span>
                          {t("timeline", { count: entry.history.length })}
                        </span>
                        <span className="text-sm text-muted-foreground/60">
                          {t("newestFirst")}
                        </span>
                      </div>

                      <div className="max-h-52 overflow-y-auto space-y-0 pr-1 relative pl-3 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border/60">
                        {entry.history.map((h, idx) => {
                          const isFirst = idx === entry.history.length - 1;
                          const isRapidPass = h.deltaMs > 0 && h.deltaMs < 50;

                          return (
                            <div
                              key={h.id}
                              className="relative flex items-center justify-between py-1.5 px-2.5 my-1 rounded-md border border-border/30 bg-background/80 hover:bg-background transition-colors group shadow-xs"
                            >
                              {/* Node Circle */}
                              <span
                                className={cn(
                                  "absolute -left-2.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-background shadow-xs",
                                  isFirst
                                    ? "bg-blue-500 ring-2 ring-blue-500/20"
                                    : isRapidPass
                                      ? "bg-amber-500 ring-2 ring-amber-500/20"
                                      : "bg-emerald-500 ring-2 ring-emerald-500/20",
                                )}
                              />

                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-6 shrink-0 text-sm font-bold text-foreground">
                                  {"#"}
                                  {h.id}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {formatRegionalTime(
                                    h.timestamp,
                                    formatLocale,
                                    timeZone,
                                  )}
                                </span>
                                {h.note && (
                                  <span className="truncate rounded border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.2 text-sm text-amber-500/90">
                                    {h.note}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                <span
                                  className={cn(
                                    "px-1.5 py-0.2 rounded text-sm font-mono border",
                                    isFirst
                                      ? "bg-blue-500/10 text-blue-500 border-blue-500/20"
                                      : isRapidPass
                                        ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
                                        : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
                                  )}
                                >
                                  {t("deltaMilliseconds", {
                                    count: h.deltaMs,
                                  })}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
